import { Injectable } from '@angular/core';
import { Coordinate } from 'ol/coordinate';
import { Polygon } from 'ol/geom';
import * as olProj from "ol/proj";

@Injectable({
	providedIn: 'root'
})
export class VicentyToolControlService {

	static readonly a: number = 6378137;
	static readonly b: number = 6356752.3142;
	static readonly f: number = 1 / 298.257223563;

	constructor() { }

	static calculateAzimuth(latitude1: number, longitude1: number, latitude2: number, longitude2: number): number {

		let lat1: number = VicentyToolControlService.degrees_to_radians(latitude1);
		let lon1: number = VicentyToolControlService.degrees_to_radians(longitude1);
		let lat2: number = VicentyToolControlService.degrees_to_radians(latitude2);
		let lon2: number = VicentyToolControlService.degrees_to_radians(longitude2);

		let L: number = lon2 - lon1;
		let U1: number = Math.atan((1.0 - VicentyToolControlService.f) * Math.tan(lat1));
		let U2: number = Math.atan((1.0 - VicentyToolControlService.f) * Math.tan(lat2));
		let sinU1: number = Math.sin(U1);
		let cosU1: number = Math.cos(U1);
		let sinU2: number = Math.sin(U2);
		let cosU2: number = Math.cos(U2);

		let lambda: number = L;
		let lambdaP: number = 2 * Math.PI;
		let iterLimit: number = 20;
		let cosSqAlpha: number = null;
		let sinSigma: number = null;
		let cos2SigmaM: number = null;
		let cosSigma: number = null;
		let sigma: number = null;

		while ((Math.abs(lambda - lambdaP) > 1e-15) && (--iterLimit > 0)) {

			let sinLambda: number = Math.sin(lambda);
			let cosLambda: number = Math.cos(lambda);
			sinSigma = Math.sqrt(cosU2 * sinLambda * cosU2 * sinLambda
				+ (cosU1 * sinU2 - (sinU1 * cosU2 * cosLambda)) * (cosU1 * sinU2 - (sinU1 * cosU2 * cosLambda)));

			if (sinSigma == 0) {
				return 0;
			}

			cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosLambda;
			sigma = Math.atan2(sinSigma, cosSigma);
			let sinAlpha: number = cosU1 * cosU2 * sinLambda / sinSigma;
			cosSqAlpha = 1 - (sinAlpha * sinAlpha);

			if (cosSqAlpha == 0) {
				cos2SigmaM = 0;
			} else {
				cos2SigmaM = cosSigma - (2 * sinU1 * sinU2 / cosSqAlpha);
			}

			let C: number = VicentyToolControlService.f / 16 * cosSqAlpha * (4 + VicentyToolControlService.f * (4 - (3 * cosSqAlpha)));
			lambdaP = lambda;
			lambda = L + (1 - C) * VicentyToolControlService.f * sinAlpha
				* (sigma
					+ C * sinSigma * (cos2SigmaM + C * cosSigma
						* (-1 + 2 * cos2SigmaM * cos2SigmaM)));
		}

		if (iterLimit == 0) {
			return null;
		}

		let forBearOut: number = Math.atan2(Math.cos(U2) * Math.sin(lambda),
			Math.cos(U1) * Math.sin(U2) - (Math.sin(U1) * Math.cos(U2) * Math.cos(lambda)));

		if (forBearOut < 0) {
			forBearOut += (2 * Math.PI);
		}

		if (forBearOut == (2 * Math.PI)) {
			forBearOut = 0;
		}

		return VicentyToolControlService.toDegrees(forBearOut);
	}

	public static calculateDistance(latitude1: number, longitude1: number, latitude2: number, longitude2: number): number {

		let L: number = VicentyToolControlService.degrees_to_radians(longitude2 - longitude1);
		let U1: number = Math.atan((1 - VicentyToolControlService.f) * Math.tan(VicentyToolControlService.degrees_to_radians(latitude1)));
		let U2: number = Math.atan((1 - VicentyToolControlService.f) * Math.tan(VicentyToolControlService.degrees_to_radians(latitude2)));
		let sinU1: number = Math.sin(U1), cosU1 = Math.cos(U1);
		let sinU2: number = Math.sin(U2), cosU2 = Math.cos(U2);
		let lambda: number = L, lambdaP, iterLimit = 100;

		let cosSqAlpha: number;
		let sinSigma: number;
		let cos2SigmaM: number;
		let cosSigma: number;
		let sigma: number;

		do {

			let sinLambda: number = Math.sin(lambda), cosLambda = Math.cos(lambda);
			sinSigma = Math.sqrt((cosU2 * sinLambda) * (cosU2 * sinLambda)
				+ (cosU1 * sinU2 - sinU1 * cosU2 * cosLambda) * (cosU1 * sinU2 - sinU1 * cosU2 * cosLambda));

			if (sinSigma == 0) {
				return 0;
			}

			cosSigma = sinU1 * sinU2 + cosU1 * cosU2 * cosLambda;
			sigma = Math.atan2(sinSigma, cosSigma);
			let sinAlpha: number = cosU1 * cosU2 * sinLambda / sinSigma;
			cosSqAlpha = 1 - sinAlpha * sinAlpha;
			cos2SigmaM = cosSigma - 2 * sinU1 * sinU2 / cosSqAlpha;

			if (isNaN(cos2SigmaM)) {
				cos2SigmaM = 0; // equatorial line: cosSqAlpha=0 (�6)
			}

			let C: number = VicentyToolControlService.f / 16 * cosSqAlpha * (4 + VicentyToolControlService.f * (4 - 3 * cosSqAlpha));
			lambdaP = lambda;
			lambda = L + (1 - C) * VicentyToolControlService.f * sinAlpha
				* (sigma + C * sinSigma * (cos2SigmaM + C * cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM)));

		} while (Math.abs(lambda - lambdaP) > 1e-12 && --iterLimit > 0);

		if (iterLimit == 0) {
			return null; // formula failed to converge
		}

		let uSq: number = cosSqAlpha * (VicentyToolControlService.a * VicentyToolControlService.a - VicentyToolControlService.b * VicentyToolControlService.b) / (VicentyToolControlService.b * VicentyToolControlService.b);
		let A: number = 1 + uSq / 16384 * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
		let B: number = uSq / 1024 * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));
		let deltaSigma: number = B * sinSigma * (cos2SigmaM + B / 4 * (cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM)
			- B / 6 * cos2SigmaM * (-3 + 4 * sinSigma * sinSigma) * (-3 + 4 * cos2SigmaM * cos2SigmaM)));
		let s = VicentyToolControlService.b * A * (sigma - deltaSigma);

		return s;
	}

	public static calculateDestinationPoint(latitude1: number, longitude1: number, azimuth: number, distance: number): number[] {
		// lat/lon must be in epsg:4326(normal lat/lon) and distance must be in meters

		let s: number = distance;
		let alpha1: number = VicentyToolControlService.degrees_to_radians(azimuth);
		let sinAlpha1: number = Math.sin(alpha1), cosAlpha1 = Math.cos(alpha1);

		let tanU1: number = (1 - VicentyToolControlService.f) * Math.tan(VicentyToolControlService.degrees_to_radians(latitude1));
		let cosU1: number = 1 / Math.sqrt((1 + tanU1 * tanU1)), sinU1 = tanU1 * cosU1;
		let sigma1: number = Math.atan2(tanU1, cosAlpha1);
		let sinAlpha: number = cosU1 * sinAlpha1;
		let cosSqAlpha: number = 1 - sinAlpha * sinAlpha;
		let uSq: number = cosSqAlpha * (VicentyToolControlService.a * VicentyToolControlService.a - VicentyToolControlService.b * VicentyToolControlService.b) / (VicentyToolControlService.b * VicentyToolControlService.b);
		let A: number = 1 + uSq / 16384 * (4096 + uSq * (-768 + uSq * (320 - 175 * uSq)));
		let B: number = uSq / 1024 * (256 + uSq * (-128 + uSq * (74 - 47 * uSq)));

		let sigma: number = s / (VicentyToolControlService.b * A), sigmaP = 2 * Math.PI;

		let sinSigma: number = 0;
		let cosSigma: number = 0;
		let cos2SigmaM: number = 0;

		while (Math.abs(sigma - sigmaP) > 1e-12) {

			cos2SigmaM = Math.cos(2 * sigma1 + sigma);
			sinSigma = Math.sin(sigma);
			cosSigma = Math.cos(sigma);
			let deltaSigma: number = B * sinSigma * (cos2SigmaM + B / 4 * (cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM)
				- B / 6 * cos2SigmaM * (-3 + 4 * sinSigma * sinSigma) * (-3 + 4 * cos2SigmaM * cos2SigmaM)));
			sigmaP = sigma;
			sigma = s / (VicentyToolControlService.b * A) + deltaSigma;
		}

		let tmp: number = sinU1 * sinSigma - cosU1 * cosSigma * cosAlpha1;
		let lat2: number = Math.atan2(sinU1 * cosSigma + cosU1 * sinSigma * cosAlpha1,
			(1 - VicentyToolControlService.f) * Math.sqrt(sinAlpha * sinAlpha + tmp * tmp));
		let lambda = Math.atan2(sinSigma * sinAlpha1, cosU1 * cosSigma - sinU1 * sinSigma * cosAlpha1);
		let C: number = VicentyToolControlService.f / 16 * cosSqAlpha * (4 + VicentyToolControlService.f * (4 - 3 * cosSqAlpha));
		let L: number = lambda - (1 - C) * VicentyToolControlService.f * sinAlpha
			* (sigma + C * sinSigma * (cos2SigmaM + C * cosSigma * (-1 + 2 * cos2SigmaM * cos2SigmaM)));

		let longitudeOut = (longitude1 + VicentyToolControlService.toDegrees(L));

		let destinationPoint: number[] = [];
		destinationPoint[0] = VicentyToolControlService.toDegrees(lat2);
		destinationPoint[1] = VicentyToolControlService.castToAngleRange(longitudeOut);

		return destinationPoint;
	}

	static castToAngleRange(alpha: number): number {
		let angle: number = VicentyToolControlService.degrees_to_radians(alpha) - (2 * Math.PI) * Math.floor(VicentyToolControlService.degrees_to_radians(alpha) / (2 * Math.PI) + 0.5);
		return VicentyToolControlService.toDegrees(angle);
	}

	static degrees_to_radians(degrees): number {
		return degrees * (Math.PI / 180);
	}

	static toDegrees(value): number {
		return value * 180 / Math.PI;
	}

	static createEllipseGeometry(center: Coordinate, radius: number, projection: olProj.ProjectionLike) {
		const ellipseCoords = this.calculateEllipsePoints(center, radius, projection);

		const ellipseGeometry = new Polygon([ellipseCoords]);

		return ellipseGeometry;
	}

	static calculateEllipsePoints(center, radius, projection) {
		const ellipsePoints = [];
		for (let azimuth: number = 0; azimuth <= 360; azimuth++) {
			let target: number[] = VicentyToolControlService.calculateDestinationPoint(center[1], center[0], azimuth, radius);
			let targetCoordinate: Coordinate = [target[1], target[0]];
			targetCoordinate = olProj.transform(targetCoordinate, 'EPSG:4326', projection);
			ellipsePoints.push(targetCoordinate);
		}
		return ellipsePoints;
	}
}